import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";

export const Manifesto = () => {
  const m = useContent().manifesto;
  const [selected, setSelected] = useState(null);

  return (
    <section id="manifesto" data-testid="manifesto-section" className="bg-ink py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-16">{m.eyebrow}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {m.chapters.map((c, i) => (
            <Reveal key={c.id || i} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => setSelected(c)}
                data-testid={`manifesto-chapter-${c.no}`}
                className="hud group relative w-full h-full text-left rounded-2xl border border-white/10 bg-black/40 p-8 min-h-[300px] flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none"
              >
                <span className="hud-tl" />
                <span className="hud-br" />
                <span className="font-display text-5xl md:text-6xl font-extrabold text-white/15 group-hover:text-gold transition-colors duration-500">
                  {c.no}
                </span>
                <div>
                  <h3 className="font-jp text-2xl font-bold tracking-tight text-white leading-tight mb-4">{c.title}</h3>
                  <p className="text-sm font-light leading-relaxed text-white/60">{c.body}</p>
                  <span className="mt-5 inline-grid place-items-center h-9 w-9 rounded-full border border-white/15 text-white/50 group-hover:border-gold group-hover:text-gold group-hover:rotate-90 transition-all duration-300">
                    <Plus size={16} />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="manifesto-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-6"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="hud relative w-full max-w-2xl rounded-2xl border border-gold/30 bg-surface p-8 md:p-12 glow-cyan"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <button onClick={() => setSelected(null)} data-testid="manifesto-modal-close" className="absolute top-6 right-6 text-white/50 hover:text-gold transition-colors">
                <X size={22} />
              </button>
              <span className="font-display text-6xl md:text-7xl font-extrabold text-gold/80 text-glow">{selected.no}</span>
              <h3 className="mt-6 font-jp text-3xl md:text-4xl font-bold tracking-tight chrome-text leading-tight">{selected.title}</h3>
              <div className="hairline my-8" />
              <p className="text-base font-light leading-relaxed text-white/75">{selected.detail}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
